import { LucideIcon } from "lucide-react";
import { Label } from "./Label";
import { twMerge } from "tailwind-merge";

export interface CardOption<TId extends string = string> {
  id: TId;
  icon: LucideIcon;
  title: string;
}

interface CardGroupInputProps<TId extends string = string> {
  label?: string;
  value: TId;
  onChange: (value: TId) => void;
  options: readonly CardOption<TId>[];
  className?: string;
  disabled?: boolean;
  testIdPrefix?: string;
}

export function CardGroupInput<TId extends string = string>({
  label,
  value,
  onChange,
  options,
  className,
  disabled,
  testIdPrefix,
}: CardGroupInputProps<TId>) {
  return (
    <div className={twMerge("flex flex-col gap-2", className)}>
      {label && <Label>{label}</Label>}
      <div className="flex gap-2">
        {options.map((option) => {
          const isSelected = option.id === value;
          const Icon = option.icon;

          return (
            <button
              key={option.id}
              type="button"
              data-testid={
                testIdPrefix ? `${testIdPrefix}-${option.id}` : undefined
              }
              onClick={() => !disabled && !isSelected && onChange(option.id)}
              disabled={disabled}
              className={twMerge(
                "rounded-12 flex flex-1 cursor-pointer flex-col items-center justify-center gap-[6px] p-[12px] transition-colors duration-150",
                isSelected
                  ? "bg-surface-nav-active text-typography-on-surface"
                  : "text-typography-primary bg-surface-base",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <Icon
                size={18}
                className={twMerge(
                  isSelected ? "text-icon-on-surface" : "text-icon-button",
                )}
              />
              <span className="text-[12px] leading-[1.58] font-semibold">
                {option.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
